import React, { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { UserIcon, FingerPrintIcon } from '@heroicons/react/24/solid'
import { Link } from 'react-router-dom'
import { isNative, isBiometricAvailable, verifyBiometric, getBiometricCredential, getBiometricDeviceId, deleteBiometricCredential } from '../services/native'
import MfaVerify from './MfaVerify'
import { listDepartments, activeDepartmentUrl } from '../services/departments'
import { addDepartment, switchToDepartment } from '../services/departmentSwitch'

function Login() {
  const { login, localLogin, biometricLogin, changePassword, error, isLoading, oktaConfigured, oktaEnabled, localAuthAvailable, authConfigFailed, forcePasswordChange, mfaPending } = useAuth()
  const [showLocalLogin, setShowLocalLogin] = useState(false)
  const [requireManagedDevice, setRequireManagedDevice] = useState(false)
  const [biometricReady, setBiometricReady] = useState(false)
  const [biometricLabel, setBiometricLabel] = useState('Sign in with Biometrics')
  const [biometricLoading, setBiometricLoading] = useState(false)
  const [biometricError, setBiometricError] = useState(null)

  useEffect(() => {
    fetch('/api/auth/pin/config')
      .then(r => r.json())
      .then(data => { if (data.success) setRequireManagedDevice(!!data.requireManagedDevice) })
      .catch(() => {})

    // Check biometric availability on native
    if (isNative()) {
      (async () => {
        try {
          const { isAvailable, biometryType } = await isBiometricAvailable()
          if (!isAvailable) return

          const cred = await getBiometricCredential()
          if (!cred) return

          setBiometricReady(true)
          // biometryType: 1 = Touch ID / Fingerprint, 2 = Face ID
          if (biometryType === 2) {
            setBiometricLabel('Sign in with Face ID')
          } else {
            setBiometricLabel('Sign in with Fingerprint')
          }
        } catch (e) {
          console.log('Biometric check failed:', e)
        }
      })()
    }
  }, [])
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [localError, setLocalError] = useState(null)
  const [localLoading, setLocalLoading] = useState(false)

  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [changeUser, setChangeUser] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const handleLocalLogin = async (e) => {
    e.preventDefault()
    setLocalError(null)
    setLocalLoading(true)

    const result = await localLogin(username, password)

    if (result.success) {
      if (result.forcePasswordChange) {
        setShowChangePassword(true)
        setChangeUser(username)
        setCurrentPassword(password)
      }
    } else {
      setLocalError(result.error)
    }
    setLocalLoading(false)
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      setLocalError('Passwords do not match')
      return
    }
    if (newPassword.length < 8) {
      setLocalError('Password must be at least 8 characters')
      return
    }

    setLocalError(null)
    setLocalLoading(true)

    const result = await changePassword(changeUser, currentPassword, newPassword)

    if (!result.success) {
      setLocalError(result.error)
    }
    setLocalLoading(false)
  }

  const handleBiometricLogin = async () => {
    setBiometricError(null)
    setBiometricLoading(true)
    try {
      const verified = await verifyBiometric('Sign in to Scenelight')
      if (!verified) {
        setBiometricLoading(false)
        return
      }

      const cred = await getBiometricCredential()
      if (!cred) {
        setBiometricError('No saved credentials found')
        setBiometricLoading(false)
        return
      }

      const deviceId = getBiometricDeviceId()
      const result = await biometricLogin(cred.token, deviceId)

      if (!result.success) {
        // Token expired or revoked — clean up device credentials
        if (result.error?.includes('expired') || result.error?.includes('Invalid')) {
          await deleteBiometricCredential()
          setBiometricReady(false)
        }
        setBiometricError(result.error || 'Biometric login failed')
      }
    } catch (e) {
      setBiometricError('Biometric login failed')
    }
    setBiometricLoading(false)
  }

  // Determine what to show
  const hasOkta = oktaConfigured === true && oktaEnabled !== false
  const hasLocal = localAuthAvailable
  const localOnly = !hasOkta && hasLocal
  const showLocal = showLocalLogin || localOnly

  // MFA verification screen
  if (mfaPending) {
    return <MfaVerify />
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center safe-screen">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/images/scenelight-logo.png" alt="Scenelight" className="w-28 h-28 mx-auto" />
          <h1 className="text-4xl text-white mt-4 tracking-widest" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>SCENELIGHT</h1>
          <p className="text-slate-400 mt-2">Fire Department Operations Platform</p>
        </div>

        {/* Login card */}
        <div className="card">
          {showChangePassword ? (
            <>
              <h2 className="text-xl font-semibold text-white mb-2 text-center">
                Change Password
              </h2>
              <p className="text-slate-400 text-sm text-center mb-6">
                You must set a new password before continuing.
              </p>

              {localError && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  {localError}
                </div>
              )}

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                    placeholder="At least 8 characters"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                    placeholder="Re-enter password"
                  />
                </div>
                <button
                  type="submit"
                  disabled={localLoading || !newPassword || !confirmPassword}
                  className="w-full btn btn-primary py-3 disabled:opacity-50"
                >
                  {localLoading ? 'Updating...' : 'Set Password'}
                </button>
              </form>
            </>
          ) : showLocal ? (
            <>
              <h2 className="text-xl font-semibold text-white mb-6 text-center">
                Sign in to continue
              </h2>

              {(error || localError) && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  {localError || error}
                </div>
              )}

              <form onSubmit={handleLocalLogin} className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                    placeholder="Enter username"
                    autoFocus
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                    placeholder="Enter password"
                    autoComplete="current-password"
                  />
                </div>
                <button
                  type="submit"
                  disabled={localLoading || !username || !password}
                  className="w-full btn btn-primary py-3 flex items-center justify-center gap-3 disabled:opacity-50"
                >
                  {localLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <span>Sign In</span>
                  )}
                </button>
              </form>

              {hasOkta && (
                <button
                  onClick={() => { setShowLocalLogin(false); setLocalError(null) }}
                  className="w-full mt-4 text-center text-sm text-slate-500 hover:text-slate-300 transition-colors"
                >
                  Sign in with Okta instead
                </button>
              )}
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-white mb-6 text-center">
                Sign in to continue
              </h2>

              {(error || biometricError) && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  {biometricError || error}
                </div>
              )}

              {/* Biometric login button (native only) */}
              {biometricReady && (
                <>
                  <button
                    onClick={handleBiometricLogin}
                    disabled={biometricLoading}
                    className="w-full btn btn-primary py-3 flex items-center justify-center gap-3 mb-4"
                  >
                    {biometricLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      <>
                        <FingerPrintIcon className="w-5 h-5" />
                        <span>{biometricLabel}</span>
                      </>
                    )}
                  </button>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 border-t border-slate-700"></div>
                    <span className="text-xs text-slate-500">or</span>
                    <div className="flex-1 border-t border-slate-700"></div>
                  </div>
                </>
              )}

              {/* Okta login button */}
              {hasOkta && (
                <button
                  onClick={login}
                  disabled={isLoading}
                  className="w-full btn btn-primary py-3 flex items-center justify-center gap-3"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <>
                      <UserIcon className="w-5 h-5" />
                      <span>Sign in with Okta</span>
                    </>
                  )}
                </button>
              )}

              {hasOkta && hasLocal && (
                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 border-t border-slate-700"></div>
                  <span className="text-xs text-slate-500">or</span>
                  <div className="flex-1 border-t border-slate-700"></div>
                </div>
              )}

              {hasLocal && (
                <button
                  onClick={() => { setShowLocalLogin(true); setLocalError(null) }}
                  className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-3"
                >
                  {hasOkta ? 'Sign in with Local Account' : 'Sign in with Username'}
                </button>
              )}

              {!hasOkta && !hasLocal && oktaConfigured === null && (
                <div className="flex items-center justify-center gap-3 py-2">
                  <div className="w-5 h-5 border-2 border-slate-600 border-t-amber-500 rounded-full animate-spin" />
                  <p className="text-slate-400 text-sm">Loading...</p>
                </div>
              )}

              {/* Two different failures that used to look the same. Being unable
                  to ask the server which sign-in methods exist is not the same
                  as being told there are none, and only one of them is worth
                  contacting an administrator about. */}
              {!hasOkta && !hasLocal && oktaConfigured !== null && authConfigFailed && (
                <div className="text-center space-y-3">
                  <p className="text-slate-400 text-sm">
                    Could not load sign-in options. The server may still be starting up.
                  </p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
                  >
                    Try again
                  </button>
                </div>
              )}

              {!hasOkta && !hasLocal && oktaConfigured !== null && !authConfigFailed && (
                <p className="text-slate-400 text-sm text-center">
                  No authentication method configured. Contact an administrator.
                </p>
              )}

            </>
          )}
        </div>

        {/* Station device link — hidden when managed devices auto-register */}
        {!requireManagedDevice && (
          <div className="mt-6 text-center">
            <Link to="/register-device" className="text-slate-600 hover:text-slate-400 text-xs transition-colors">
              Register as Station Device
            </Link>
          </div>
        )}

        {/* Other departments on this device, and adding one -- native only.
            Leaving this department keeps it (and its Face ID) on the device. */}
        {isNative() && (
          <div className="mt-4 text-center space-y-2">
            {listDepartments().filter(d => d.url !== activeDepartmentUrl()).map(d => (
              <button
                key={d.url}
                onClick={() => switchToDepartment(d.url)}
                className="block w-full text-slate-500 hover:text-slate-300 text-xs transition-colors"
              >
                Switch to {d.name || d.url.replace(/^https?:\/\//, '')}
              </button>
            ))}
            <button
              onClick={() => addDepartment()}
              className="text-slate-600 hover:text-slate-400 text-xs transition-colors"
            >
              Change Department
            </button>
          </div>
        )}

        {/* Footer */}
        <p className="mt-4 text-center text-xs text-slate-600">
          &copy; 2026 Scenelight &bull; Version 1.0.0
        </p>
      </div>
    </div>
  )
}

export default Login
